/* tslint:disable:variable-name no-namespace */
import {
  Account,
  Address,
  Asset,
  Contract,
  FeeBumpTransaction,
  Keypair,
  StrKey,
  Transaction,
  nativeToScVal,
  scValToNative,
} from "../base/index.js";
import { assertNotMuxed } from "../base/address.js";

import {
  concatUint8Arrays,
  hexToUint8Array,
  stringToUint8Array,
} from "uint8array-extras";
import { base64ToUint8Array } from "../base/util/base64.js";
import type { TransactionBuilder } from "../base/index.js";
import { createHttpClient } from "./axios.js";
import type { Api as FriendbotApi } from "../friendbot/index.js";
import * as jsonrpc from "./jsonrpc.js";
import { Api } from "./api.js";
import { assembleTransaction } from "./transaction.js";
import {
  parseRawSendTransaction,
  parseRawSimulation,
  parseRawLedgerEntries,
  parseRawEvents,
  parseRawQueryEvents,
  parseRawTransactions,
  parseTransactionInfo,
  parseRawLedger,
  parseRawLatestLedger,
} from "./parsers.js";
import { Utils } from "../utils.js";
import type { HttpClient } from "../http-client/index.js";
import {
  AccountEntry,
  ClaimableBalanceEntry,
  ClaimableBalanceId,
  ContractDataDurability,
  ContractExecutableExternalRef,
  Hash,
  LedgerKey,
  LedgerKeyAccount,
  LedgerKeyClaimableBalance,
  LedgerKeyContractCode,
  LedgerKeyContractData,
  LedgerKeyTrustLine,
  OperationMeta,
  ScAddress,
  ScContractInstance,
  ScSpecTypeDef,
  ScVal,
  TransactionMeta,
  TransactionMetaV4,
  TrustLineEntry,
} from "../xdr/index.js";

/**
 * Default transaction submission timeout for RPC requests, in milliseconds
 * @defaultValue 60000
 */
export const SUBMIT_TRANSACTION_TIMEOUT = 60 * 1000;

/**
 * Specifies the durability namespace of contract-related ledger entries.
 *
 * @see {@link https://developers.stellar.org/docs/learn/smart-contract-internals/state-archival | State Archival docs}
 * @see {@link https://docs.rs/soroban-sdk/latest/soroban_sdk/storage/struct.Storage.html | Rust SDK Storage docs}
 */
export enum Durability {
  Temporary = "temporary",
  Persistent = "persistent",
}

export namespace RpcServer {
  /**
   * @deprecated Use `Api.GetEventsRequest` instead.
   * @see {@link Api.GetEventsRequest}
   */
  export type GetEventsRequest = Api.GetEventsRequest;

  export interface PollingOptions {
    attempts?: number;
    sleepStrategy?: SleepStrategy;
  }

  /**
   * Describes additional resource leeways for transaction simulation.
   */
  export interface ResourceLeeway {
    /** Simulate the transaction with more CPU instructions available. */
    cpuInstructions: number;
  }

  /**
   * Options for configuring connections to RPC servers.
   */
  export interface Options {
    /** Allow connecting to http servers, default: `false`. This must be set to false in production deployments! */
    allowHttp?: boolean;
    /** Timeout for each request, in whole milliseconds, at most 2147483647. `0` or unset means no limit. */
    timeout?: number;
    /** Additional headers that should be added to any requests to the RPC server. A redirect to another origin can receive these headers. In the Fetch build, set `maxRedirects` on `server.httpClient.defaults` to strip them in Node, or `maxRedirects: 0` to refuse redirects. */
    headers?: Record<string, string>;
  }
}

const DEFAULT_GET_TRANSACTION_TIMEOUT: number = 30;

// Node's timers overflow above this and fire at once, so a larger request
// timeout would end every request almost immediately.
export const MAX_TIMEOUT_MS = 2 ** 31 - 1;

// "Set" means neither undefined nor null.
function isSet(value: unknown): boolean {
  return value !== undefined && value !== null;
}

// The server decodes ledgers as uint32 and limit as uint.
function encodeQueryEventsNumber(
  name: string,
  value: unknown,
  max: number = Number.MAX_SAFE_INTEGER,
): number {
  if (
    !Number.isInteger(value) ||
    (value as number) < 0 ||
    (value as number) > max
  ) {
    throw new TypeError(
      `queryEvents ${name} must be an integer from 0 to ${max}`,
    );
  }
  return value as number;
}

function encodeQueryEventsTopic(name: string, topic: unknown): string {
  if (topic === "*") {
    throw new TypeError(
      `queryEvents filter ${name} cannot be "*"; omit it to match any value`,
    );
  }
  if (typeof topic === "string") {
    return topic;
  }
  // Duck-typed: the built package has several ScVal copies, so `instanceof`
  // would reject an ScVal from another copy.
  if (
    typeof topic === "object" &&
    topic !== null &&
    typeof (topic as { toXdr?: unknown }).toXdr === "function"
  ) {
    return (topic as { toXdr(format: "base64"): string }).toXdr("base64");
  }
  throw new TypeError(
    `queryEvents filter ${name} must be a base64 string or an ScVal`,
  );
}

function encodeQueryEventsFilter(filter: unknown): Api.RawQueryEventsFilter {
  if (typeof filter !== "object" || filter === null) {
    throw new TypeError("queryEvents filter must be an object");
  }
  const f = filter as Api.QueryEventsFilter;
  const out: Api.RawQueryEventsFilter = {};
  if (isSet(f.type) && f.type !== ("" as unknown)) {
    out.type = f.type;
  }
  if (isSet(f.contractId) && f.contractId !== "") {
    out.contractId = f.contractId;
  }
  for (const key of ["topic0", "topic1", "topic2", "topic3"] as const) {
    if (isSet(f[key]) && f[key] !== "") {
      out[key] = encodeQueryEventsTopic(key, f[key]);
    }
  }
  return out;
}

function encodeQueryEventsRequest(
  request: Api.QueryEventsRequest,
): Api.RawQueryEventsRequest {
  const r = request as Record<string, unknown>;
  const rangeKeySet = ["minLedger", "maxLedger", "order", "filters"].some(
    (key) => isSet(r[key]),
  );
  // A set cursor must be valid; an unset one is only allowed next to a range.
  if (
    "cursor" in r &&
    (isSet(r.cursor) || !rangeKeySet) &&
    (typeof r.cursor !== "string" || r.cursor === "")
  ) {
    throw new TypeError("queryEvents cursor must be a non-empty string");
  }

  const out: Record<string, unknown> = {};
  for (const key of ["minLedger", "maxLedger"] as const) {
    if (isSet(r[key])) {
      const value = encodeQueryEventsNumber(key, r[key], 2 ** 32 - 1);
      if (value !== 0) {
        out[key] = value;
      }
    }
  }
  if (isSet(r.order) && r.order !== "") {
    out.order = r.order;
  }
  if (isSet(r.filters)) {
    if (!Array.isArray(r.filters)) {
      throw new TypeError("queryEvents filters must be an array");
    }
    out.filters = (r.filters as unknown[]).map(encodeQueryEventsFilter);
  }
  if (isSet(r.cursor)) {
    out.cursor = r.cursor;
  }
  if (isSet(r.limit)) {
    out.limit = encodeQueryEventsNumber("limit", r.limit);
  }
  return out as Api.RawQueryEventsRequest;
}

/// A strategy that will sleep 1 second each time

export const BasicSleepStrategy: SleepStrategy = (_iter: number) => 1000;

/// A strategy that will sleep 1 second longer on each attempt
export const LinearSleepStrategy: SleepStrategy = (iter: number) => 1000 * iter;

/**
 * A function that returns the number of *milliseconds* to sleep
 * on a given `iter`ation.
 */
export type SleepStrategy = (iter: number) => number;

function findCreatedAccountSequenceInTransactionMeta(
  meta: TransactionMeta,
): string {
  let operations: OperationMeta[] = [];
  switch (meta.type) {
    case "operations":
      operations = meta.value;
      break;
    case "v1":
    case "v2":
    case "v3":
    case "v4": // all four have the same interface
      operations = (meta.value as TransactionMetaV4).operations;
      break;
    default:
      throw new Error(
        // @ts-expect-error this should be unreachable if the XDR types are correct, but we throw just in case
        "Unexpected transaction meta switch value variant: " + meta.type,
      );
  }
  const created = operations
    .flatMap((op) => op.changes)
    .find(
      (c) => c.type === "ledgerEntryCreated" && c.value.data.type === "account",
    );
  if (created && created.type === "ledgerEntryCreated") {
    const entryData = created.value.data;
    if (entryData.type === "account") {
      return entryData.value.seqNum.toString();
    }
  }
  throw new Error("No account created in transaction");
}

function containsMuxedAddress(val: ScVal): boolean {
  switch (val.type) {
    case "scvAddress":
      return (
        val.value.type === "scAddressTypeMuxedAccount" ||
        val.value.type === "scAddressTypeMuxedContract"
      );
    case "scvVec":
      return (val.value ?? []).some(containsMuxedAddress);
    case "scvMap":
      return (val.value ?? []).some(
        (e) => containsMuxedAddress(e.key) || containsMuxedAddress(e.val),
      );
    default:
      return false;
  }
}

/**
 * Best-effort, human-readable name for a contract spec type, e.g.
 * `scSpecTypeU32` becomes `U32` and `scSpecTypeAddress` becomes `Address`.
 * User-defined types (structs/enums/unions) are shown by their declared name.
 */
function contractSpecTypeName(td: ScSpecTypeDef): string {
  if (td.type === "scSpecTypeUdt") {
    return td.value.name.toString();
  }
  return td.type.replace(/^scSpecType/, "");
}

/**
 * Handles the network connection to a Soroban RPC instance, exposing an
 * interface for requests to that instance.
 *
 *
 * @param serverURL - Soroban-RPC Server URL (ex. `http://localhost:8000/soroban/rpc`).
 * @param opts - (optional) Options object
 *   - `allowHttp` (optional): Allows connecting to insecure http servers
 *    (default: `false`). This must be set to false in production deployments!
 *   - `timeout` (optional): Timeout for each request, in whole milliseconds,
 *    at most 2147483647. `0` or unset means no limit.
 *   - `headers` (optional): Allows setting custom headers
 *
 * @see {@link https://developers.stellar.org/docs/data/rpc/api-reference/methods | API reference docs}
 */
export class RpcServer {
  public readonly serverURL: URL;
  /**
   * HTTP client instance for making requests to Horizon.
   * Exposes interceptors, defaults, and other configuration options.
   *
   * @example
   * ```ts
   * // Add authentication header
   * server.httpClient.defaults.headers['Authorization'] = 'Bearer token';
   *
   * // Add request interceptor
   * server.httpClient.interceptors.request.use((config) => {
   *   console.log('Request:', config.url);
   *   return config;
   * });
   * ```
   */
  public readonly httpClient: HttpClient;
  constructor(serverURL: string, opts: RpcServer.Options = {}) {
    /**
     * RPC Server URL (ex. `http://localhost:8000/soroban/rpc`).
     */
    this.serverURL = new URL(serverURL);
    const { timeout } = opts;
    if (
      timeout !== undefined &&
      !(Number.isInteger(timeout) && timeout >= 0 && timeout <= MAX_TIMEOUT_MS)
    ) {
      throw new Error(
        `timeout must be 0 or a whole number of milliseconds up to ${MAX_TIMEOUT_MS}, got ${String(timeout)}`,
      );
    }
    this.httpClient = createHttpClient(opts.headers, timeout);
    if (this.serverURL.protocol !== "https:" && !opts.allowHttp) {
      throw new Error(
        "Cannot connect to insecure Soroban RPC server if `allowHttp` isn't set",
      );
    }
  }

  /**
   * Fetch a minimal set of current info about a Stellar account.
   *
   * Needed to get the current sequence number for the account so you can build
   * a successful transaction with {@link TransactionBuilder}.
   *
   * @param address - The public address of the account to load.
   * @returns A promise which resolves to the {@link Account}
   * object with a populated sequence number
   *
   * @see {@link https://developers.stellar.org/docs/data/rpc/api-reference/methods/getLedgerEntries | getLedgerEntries docs}
   *
   * @example
   * ```ts
   * const accountId = "GBZC6Y2Y7Q3ZQ2Y4QZJ2XZ3Z5YXZ6Z7Z2Y4QZJ2XZ3Z5YXZ6Z7Z2Y4";
   * server.getAccount(accountId).then((account) => {
   *   console.log("sequence:", account.sequence);
   * });
   * ```
   */
  public async getAccount(address: string): Promise<Account> {
    const entry = await this.getAccountEntry(address);
    return new Account(address, entry.seqNum.toString());
  }

  /**
   * Fetch the full account entry for a Stellar account.
   *
   * @param address - The public address of the account to load.
   * @returns Resolves to the full on-chain account
   *    entry
   *
   * @see
   * {@link https://developers.stellar.org/docs/data/rpc/api-reference/methods/getLedgerEntries | getLedgerEntries docs}
   *
   * @example
   * ```ts
   * const accountId = "GBZC6Y2Y7Q3ZQ2Y4QZJ2XZ3Z5YXZ6Z7Z2Y4QZJ2XZ3Z5YXZ6Z7Z2Y4";
   * server.getAccountEntry(accountId).then((account) => {
   *   console.log("sequence:", account.balance().toString());
   * });
   * ```
   */
  public async getAccountEntry(address: string): Promise<AccountEntry> {
    const ledgerKey = LedgerKey.account(
      new LedgerKeyAccount({
        accountId: Keypair.fromPublicKey(address).xdrPublicKey(),
      }),
    );

    try {
      const resp = await this.getLedgerEntry(ledgerKey);
      if (resp.val.type !== "account") {
        throw new Error(
          "unexpected ledger entry type for account: " + resp.val.type,
        );
      }
      return resp.val.value;
    } catch {
      throw new Error(`Account not found: ${address}`);
    }
  }

  /**
   * Fetch the full trustline entry for a Stellar account.
   *
   * @param account - The public address of the account whose trustline it is
   * @param asset - The trustline's asset
   * @returns Resolves to the full on-chain trustline
   *    entry
   *
   * @see
   * {@link https://developers.stellar.org/docs/data/rpc/api-reference/methods/getLedgerEntries | getLedgerEntries docs}
   *
   * @deprecated Use {@link getAssetBalance}, instead
   * @example
   * ```ts
   * const accountId = "GBZC6Y2Y7Q3ZQ2Y4QZJ2XZ3Z5YXZ6Z7Z2Y4QZJ2XZ3Z5YXZ6Z7Z2Y4";
   * const asset = new Asset(
   *  "USDC",
   *  "GBBD47IF6LWK7P7MDEVSCWR7DPUWV3NY3DTQEVFL4NAT4AQH3ZLLFLA5"
   * );
   * server.getTrustline(accountId, asset).then((entry) => {
   *   console.log(`{asset.toString()} balance for ${accountId}:", entry.balance().toString());
   * });
   * ```
   */
  public async getTrustline(
    account: string,
    asset: Asset,
  ): Promise<TrustLineEntry> {
    const trustlineLedgerKey = LedgerKey.trustline(
      new LedgerKeyTrustLine({
        accountId: Keypair.fromPublicKey(account).xdrAccountId(),
        asset: asset.toTrustLineXdrObject(),
      }),
    );

    try {
      const entry = await this.getLedgerEntry(trustlineLedgerKey);
      if (entry.val.type !== "trustline") {
        throw new Error(
          "unexpected ledger entry type for trustline: " + entry.val.type,
        );
      }
      return entry.val.value;
    } catch {
      throw new Error(
        `Trustline for ${asset.getCode()}:${asset.getIssuer()} not found for ${account}`,
      );
    }
  }

  /**
   * Fetch the full claimable balance entry for a Stellar account.
   *
   * @param id - The strkey (`B...`) or hex (`00000000abcde...`) (both
   *    IDs with and without the 000... version prefix are accepted) of the
   *    claimable balance to load
   * @returns Resolves to the full on-chain
   *    claimable balance entry
   *
   * @see
   * {@link https://developers.stellar.org/docs/data/rpc/api-reference/methods/getLedgerEntries | getLedgerEntries docs}
   *
   * @example
   * ```ts
   * const id = "00000000178826fbfe339e1f5c53417c6fedfe2c05e8bec14303143ec46b38981b09c3f9";
   * server.getClaimableBalance(id).then((entry) => {
   *   console.log(`Claimable balance {id.substr(0, 12)} has:`);
   *   console.log(`  asset:  ${Asset.fromOperation(entry.asset).toString()}`);
   *   console.log(`  amount: ${entry.amount.toString()}`);
   * });
   * ```
   */
  public async getClaimableBalance(id: string): Promise<ClaimableBalanceEntry> {
    let balanceId;
    if (StrKey.isValidClaimableBalance(id)) {
      const buffer = StrKey.decodeClaimableBalance(id);

      // Pad the version byte to be a full int32 like in the XDR spec
      const v = concatUint8Arrays([new Uint8Array(3), buffer.subarray(0, 1)]);

      // Slap on the rest of it and decode it
      balanceId = ClaimableBalanceId.fromXdr(
        concatUint8Arrays([v, buffer.subarray(1)]),
      );
    } else if (id.match(/[a-f0-9]{72}/i)) {
      balanceId = ClaimableBalanceId.fromXdr(id, "hex");
    } else if (id.match(/[a-f0-9]{64}/i)) {
      balanceId = ClaimableBalanceId.fromXdr(id.padStart(72, "0"), "hex");
    } else {
      throw new TypeError(`expected 72-char hex ID or strkey, not ${id}`);
    }

    const trustlineLedgerKey = LedgerKey.claimableBalance(
      new LedgerKeyClaimableBalance({ balanceId: balanceId }),
    );

    try {
      const entry = await this.getLedgerEntry(trustlineLedgerKey);
      if (entry.val.type !== "claimableBalance") {
        throw new Error(
          "unexpected ledger entry type for claimable balance: " +
            entry.val.type,
        );
      }
      return entry.val.value;
    } catch {
      throw new Error(`Claimable balance ${id} not found`);
    }
  }

  /**
   * Fetch the balance of an asset held by an account or contract.
   *
   * The `address` argument may be provided as a string (as a {@link StrKey}),
   * {@link Address}, or {@link Contract}.
   *
   * @param address - The account or contract whose
   *    balance should be fetched.
   * @param asset - The asset whose balance you want to inspect.
   * @param networkPassphrase - (optional) optionally, when requesting the
   *    balance of a contract, the network passphrase to which this token
   *    applies. If omitted and necessary, a request about network information
   *    will be made (see {@link getNetwork}), since contract IDs for assets are
   *    specific to a network. You can refer to {@link Networks} for a list of
   *    built-in passphrases, e.g., `Networks.TESTNET`.
   * @returns Resolves with balance entry details
   *    when available.
   *
   * @throws If the supplied `address` is not a valid account or contract
   *    strkey, or is a muxed address (`M...` or `W...`).
   *
   * @example
   * ```ts
   * const usdc = new Asset(
   *   "USDC",
   *   "GBBD47IF6LWK7P7MDEVSCWR7DPUWV3NY3DTQEVFL4NAT4AQH3ZLLFLA5"
   * );
   * const balance = await server.getAssetBalance("GD...", usdc);
   * console.log(balance.balanceEntry?.amount);
   * ```
   */
  public async getAssetBalance(
    address: string | Address | Contract,
    asset: Asset,
    networkPassphrase?: string,
  ): Promise<Api.BalanceResponse> {
    let addr: string = address as string;
    // Coalesce to a strkey
    if (typeof address === "string") {
      addr = address;
    } else if (address instanceof Address) {
      addr = address.toString();
    } else if (address instanceof Contract) {
      addr = address.toString();
    } else {
      // shouldn't happen, but be defensive
      throw new TypeError(`invalid address: ${address}`);
    }

    if (StrKey.isValidEd25519PublicKey(addr)) {
      const [tl, ll] = await Promise.all([
        this.getTrustline(addr, asset),
        this.getLatestLedger(),
      ]);

      return {
        latestLedger: ll.sequence,
        balanceEntry: {
          amount: tl.balance.toString(),
          // Extract actual flags from the coalesced value.
          authorized: Boolean(tl.flags & 0x1), // AUTHORIZED_FLAG
          clawback: Boolean(tl.flags & 0x4), // TRUSTLINE_CLAWBACK_ENABLED_FLAG
          authorizedToMaintainLiabilities: Boolean(tl.flags & 0x2), // AUTHORIZED_TO_MAINTAIN_LIABILITIES_FLAG
          revocable: Boolean(tl.flags & 0x2), // AUTHORIZED_TO_MAINTAIN_LIABILITIES_FLAG (deprecated, will be removed in a future major release)
        },
      };
    } else if (StrKey.isValidContract(addr)) {
      return this.getSACBalance(addr, asset, networkPassphrase);
    }
    // Balances are held by the base address, never per mux ID.
    assertNotMuxed(addr, "has no balance of its own; use its base address");

    throw new Error(`invalid address: ${address}`);
  }

  /**
   * General node health check.
   *
   * @returns A promise which resolves to the
   * {@link Api.GetHealthResponse} object with the status of the
   * server (e.g. "healthy").
   *
   * @see {@link https://developers.stellar.org/docs/data/rpc/api-reference/methods/getHealth | getLedgerEntries docs}
   *
   * @example
   * ```ts
   * server.getHealth().then((health) => {
   *   console.log("status:", health.status);
   * });
   * ```
   */

  public async getHealth(): Promise<Api.GetHealthResponse> {
    return jsonrpc.postObject<Api.GetHealthResponse>(
      this.httpClient,
      this.serverURL.toString(),
      "getHealth",
    );
  }

  /**
   * Reads the current value of contract data ledger entries directly.
   *
   * Allows you to directly inspect the current state of a contract. This is a
   * backup way to access your contract data which may not be available via
   * events or {@link rpc.Server.simulateTransaction}.
   *
   * @param contract - The contract ID containing the
   *    data to load as a strkey (`C...` form), a {@link Contract}, or an
   *    {@link Address} instance
   * @param key - The key of the contract data to load
   * @param durability - (optional) The "durability
   *    keyspace" that this ledger key belongs to, which is either 'temporary'
   *    or 'persistent' (the default), see {@link rpc.Durability}.
   * @returns The current data value
   *
   * @throws If `contract` is a muxed address, or `key` contains a muxed
   *    address (`M...` or `W...`).
   *
   * **Warning:** If the data entry in question is a 'temporary' entry, it's
   * entirely possible that it has expired out of existence.
   *
   * @see {@link https://developers.stellar.org/docs/data/rpc/api-reference/methods/getLedgerEntries | getLedgerEntries docs}
   *
   * @example
   * ```ts
   * const contractId = "CCJZ5DGASBWQXR5MPFCJXMBI333XE5U3FSJTNQU7RIKE3P5GN2K2WYD5";
   * const key = xdr.ScVal.scvSymbol("counter");
   * server.getContractData(contractId, key, Durability.Temporary).then(data => {
   *   console.log("value:", data.val);
   *   console.log("liveUntilLedgerSeq:", data.liveUntilLedgerSeq);
   *   console.log("lastModified:", data.lastModifiedLedgerSeq);
   *   console.log("latestLedger:", data.latestLedger);
   * });
   * ```
   */

  public async getContractData(
    contract: string | Address | Contract,
    key: ScVal,
    durability: Durability = Durability.Persistent,
  ): Promise<Api.LedgerEntryResult> {
    // coalesce `contract` param variants to an ScAddress
    let scAddress: ScAddress;
    // Muxed addresses can never own contract data.
    if (typeof contract === "string" || contract instanceof Address) {
      assertNotMuxed(
        contract,
        "cannot own contract data; use its base address",
      );
    }
    if (typeof contract === "string") {
      scAddress = new Contract(contract).address().toScAddress();
    } else if (contract instanceof Address) {
      scAddress = contract.toScAddress();
    } else if (contract instanceof Contract) {
      scAddress = contract.address().toScAddress();
    } else {
      throw new TypeError(`unknown contract type: ${contract}`);
    }

    // The host rejects muxed addresses anywhere in a storage key.
    if (containsMuxedAddress(key)) {
      throw new TypeError(
        "contract data keys cannot contain muxed addresses (M... or W...)",
      );
    }

    let xdrDurability: ContractDataDurability;
    switch (durability) {
      case Durability.Temporary:
        xdrDurability = ContractDataDurability.temporary;
        break;

      case Durability.Persistent:
        xdrDurability = ContractDataDurability.persistent;
        break;

      default:
        throw new TypeError(`invalid durability: ${durability}`);
    }

    const contractKey = LedgerKey.contractData(
      new LedgerKeyContractData({
        key,
        contract: scAddress,
        durability: xdrDurability,
      }),
    );

    // `getLedgerEntries` rejects only on transport failure, so a 429, a 5xx or
    // a timeout propagates to the caller untouched. Absence is then decided
    // here, by the entry count. The previous `try/catch` around
    // `getLedgerEntry` collapsed both routes into the same 404, which turned
    // every rate-limited poll into "this entry is gone". Callers branch on that
    // to drive deployment status, eviction detection and cache invalidation, so
    // a misclassified transport error became a wrong state transition instead
    // of a retry.
    const { entries } = await this.getLedgerEntries(contractKey);
    if (entries.length !== 1) {
      // Shape unchanged on purpose: `e.code === 404` keeps matching exactly
      // when the entry is absent, which is the only case it ever meant.
      throw {
        code: 404,
        message: `Contract data not found for ${Address.fromScAddress(
          scAddress,
        ).toString()} with key ${key.toXdr("base64")} and durability: ${durability}`,
      };
    }
    return entries[0];
  }

  /**
   * Retrieves the deployed contract instance for a given contract ID.
   *
   * The instance describes the contract's executable — either a Wasm hash or
   * the built-in Stellar Asset Contract — along with its instance storage.
   *
   * @param contractId - The contract ID (`C...`) to look up
   * @returns The contract's `xdr.ScContractInstance`
   * @throws If the contract instance cannot be found on the network.
   *
   * @example
   * ```ts
   * const instance = await server.getContractInstance(
   *   "CCJZ5DGASBWQXR5MPFCJXMBI333XE5U3FSJTNQU7RIKE3P5GN2K2WYD5",
   * );
   * console.log(instance.executable.type);
   * ```
   */
  public async getContractInstance(
    contractId: string,
  ): Promise<ScContractInstance> {
    const contractLedgerKey = new Contract(contractId).getFootprint();
    const response = await this.getLedgerEntries(contractLedgerKey);
    if (!response.entries.length || !response.entries[0]?.val) {
      return Promise.reject({
        code: 404,
        message: "Could not obtain contract instance from server",
      });
    }

    const ledgerEntryData = response.entries[0].val;
    if (ledgerEntryData.type !== "contractData") {
      return Promise.reject({
        code: 404,
        message: "Expected contractData ledger entry",
      });
    }
    const scv = ledgerEntryData.value.val;
    if (scv.type !== "scvContractInstance") {
      return Promise.reject({
        code: 404,
        message: "Expected contract instance",
      });
    }
    return scv.value;
  }

  /**
   * Resolves a CAP-85 external executable reference to the Wasm hash it names.
   *
   * A contract created with an external reference does not carry its own code
   * hash. Instead the reference names an owner contract and a tag, and the
   * owner holds a *persistent* contract data entry keyed by that tag whose
   * value is the 32-byte hash of an existing Wasm. This performs exactly that
   * lookup — the owner contract is not invoked.
   *
   * @param ref - the external reference, e.g. from the
   *    `contractExecutableExternalRef` arm of a contract instance's executable
   * @returns the 32-byte Wasm hash the reference resolves to
   * @throws If the owner is not a contract, the tag entry is missing or
   *    archived, or the entry does not hold a 32-byte hash.
   *
   * @example
   * ```ts
   * const instance = await server.getContractInstance(contractId);
   * if (instance.executable.type === "contractExecutableExternalRef") {
   *   const hash = await server.getExternalRefWasmHash(
   *     instance.executable.externalRef,
   *   );
   *   const wasm = await server.getContractWasmByHash(hash);
   * }
   * ```
   */
  public async getExternalRefWasmHash(
    ref: ContractExecutableExternalRef,
  ): Promise<Uint8Array> {
    const owner = ref.executableOwner;
    if (owner.type !== "scAddressTypeContract") {
      return Promise.reject({
        code: 400,
        message:
          `External executable owner ${Address.fromScAddress(owner)} is not a ` +
          `contract, so it cannot hold the tag entry that names the Wasm`,
      });
    }

    // The tag is an unbounded SCString and may be binary, so pass it through
    // as-is rather than decoding it — a lenient decode would build a key for a
    // different entry.
    const entry = await this.getContractData(
      Address.fromScAddress(owner),
      ScVal.scvExecutableTag(ref.tag),
      Durability.Persistent,
    );

    if (entry.val.type !== "contractData") {
      return Promise.reject({
        code: 404,
        message: "Expected contractData ledger entry",
      });
    }

    const scv = entry.val.value.val;
    if (scv.type !== "scvBytes" || scv.bytes.value.length !== 32) {
      return Promise.reject({
        code: 404,
        message:
          `External executable tag entry on ${Address.fromScAddress(owner)} ` +
          `does not hold a 32-byte Wasm hash`,
      });
    }

    return scv.bytes.value;
  }

  /**
   * Retrieves the WASM bytecode for a given contract.
   *
   * This method allows you to fetch the WASM bytecode associated with a contract
   * deployed on the Soroban network. The WASM bytecode represents the executable
   * code of the contract.
   *
   * This only works for Wasm-based contracts, including one created from a
   * CAP-85 external executable reference, whose reference is resolved to a Wasm
   * hash first (see {@link getExternalRefWasmHash}). A built-in Stellar Asset
   * Contract (SAC) has no Wasm bytecode on-chain, so this throws for a SAC; use
   * {@link contract.Client.from} to build a client from the embedded SAC spec.
   *
   * @param contractId - The contract ID containing the WASM bytecode to retrieve
   * @returns A Uint8Array containing the WASM bytecode
   * @throws If the contract or its associated WASM bytecode cannot be
   * found on the network, or if the contract is a Stellar Asset Contract (SAC).
   *
   * @example
   * ```ts
   * const contractId = "CCJZ5DGASBWQXR5MPFCJXMBI333XE5U3FSJTNQU7RIKE3P5GN2K2WYD5";
   * server.getContractWasmByContractId(contractId).then(wasmBytes => {
   *   console.log("WASM bytecode length:", wasmBytes.length);
   *   // ... do something with the WASM bytecode ...
   * }).catch(err => {
   *   console.error("Error fetching WASM bytecode:", err);
   * });
   * ```
   */
  public async getContractWasmByContractId(
    contractId: string,
  ): Promise<Uint8Array> {
    const instance = await this.getContractInstance(contractId);

    const executable = instance.executable;
    if (executable.type === "contractExecutableStellarAsset") {
      return Promise.reject({
        code: 400,
        message:
          `Contract ${contractId} is a Stellar Asset Contract (SAC), which has ` +
          `no Wasm bytecode. Use contract.Client.from() to build a client from ` +
          `the built-in SAC spec instead.`,
      });
    }
    if (executable.type === "contractExecutableExternalRef") {
      // A CAP-85 reference names its code indirectly; resolve the tag entry on
      // the owner contract to get the hash.
      return this.getContractWasmByHash(
        await this.getExternalRefWasmHash(executable.externalRef),
      );
    }
    if (executable.type !== "contractExecutableWasm") {
      return Promise.reject({
        code: 404,
        message: `Contract is not a wasm executable`,
      });
    }

    return this.getContractWasmByHash(executable.value.value);
  }

  /**
   * Retrieves the WASM bytecode for a given contract hash.
   *
   * This method allows you to fetch the WASM bytecode associated with a contract
   * deployed on the Soroban network using the contract's WASM hash. The WASM bytecode
   * represents the executable code of the contract.
   *
   * @param wasmHash - The WASM hash of the contract
   * @returns A Uint8Array containing the WASM bytecode
   * @throws If the contract or its associated WASM bytecode cannot be
   * found on the network.
   *
   * @example
   * ```ts
   * const wasmHash = hexToUint8Array("...");
   * server.getContractWasmByHash(wasmHash).then(wasmBytes => {
   *   console.log("WASM bytecode length:", wasmBytes.length);
   *   // ... do something with the WASM bytecode ...
   * }).catch(err => {
   *   console.error("Error fetching WASM bytecode:", err);
   * });
   * ```
   */
  public async getContractWasmByHash(
    wasmHash: Uint8Array | string,
    format: undefined | "hex" | "base64" = undefined,
  ): Promise<Uint8Array> {
    let wasmHashBytes: Uint8Array;
    if (typeof wasmHash === "string") {
      if (format === "base64") {
        wasmHashBytes = base64ToUint8Array(wasmHash);
      } else if (format === "hex") {
        wasmHashBytes = hexToUint8Array(wasmHash);
      } else {
        wasmHashBytes = stringToUint8Array(wasmHash);
      }
    } else {
      wasmHashBytes = wasmHash;
    }

    const ledgerKeyWasmHash = LedgerKey.contractCode(
      new LedgerKeyContractCode({
        hash: new Hash(wasmHashBytes),
      }),
    );

    const responseWasm = await this.getLedgerEntries(ledgerKeyWasmHash);
    if (!responseWasm.entries.length || !responseWasm.entries[0]?.val) {
      return Promise.reject({
        code: 404,
        message: "Could not obtain contract wasm from server",
      });
    }
    const wasmEntry = responseWasm.entries[0].val;
    if (wasmEntry.type !== "contractCode") {
      return Promise.reject({
        code: 404,
        message: "Expected contractCode ledger entry",
      });
    }
    return wasmEntry.value.code;
  }

  /**
   * Performs a read-only call to a contract method and returns the decoded result.
   *
   * This is a convenience wrapper for one-line contract state queries: it builds
   * a {@link contract.Client} for the contract, simulates the method call, and
   * returns the spec-decoded return value — no manual transaction assembly,
   * signing, or submission required.
   *
   * Works for both Wasm contracts and built-in Stellar Asset Contracts (SACs):
   * the embedded SAC spec is used automatically for SACs (see
   * {@link contract.Client.from}). The query reuses this server's transport
   * (headers, interceptors, `allowHttp`).
   *
   * @typeParam T - the expected (decoded) return type of the method
   * @param contractId - the contract to query (`C...`)
   * @param method - the contract method to call
   * @param args - named arguments for the method, keyed by parameter name
   *    (omit for methods that take no arguments)
   * @param networkPassphrase - (optional) the network passphrase. If omitted, a
   *    request about network information will be made (see {@link getNetwork}).
   *    You can refer to {@link Networks} for a list of built-in passphrases,
   *    e.g., `Networks.TESTNET`.
   * @returns An object with the method's decoded return value (`result`) and
   *    `isReadCall`: whether this specific call is a side-effect-free read that
   *    needs no signature (it wrote no state and required no authorization).
   *    `isReadCall` is per-call, not per-method: it reflects the given `args`.
   *    Since `queryContract` never signs or sends, `isReadCall: false` means the
   *    `result` is a simulation preview of a call that would change state.
   * @throws If the contract has no such method, or if the simulation fails.
   *
   * @example
   * ```ts
   * const { result: decimals, isReadCall } = await server.queryContract<number>(
   *   "CCJZ5DGASBWQXR5MPFCJXMBI333XE5U3FSJTNQU7RIKE3P5GN2K2WYD5",
   *   "decimals",
   * );
   * const { result: balance } = await server.queryContract<bigint>(
   *   "CCJZ5DGASBWQXR5MPFCJXMBI333XE5U3FSJTNQU7RIKE3P5GN2K2WYD5",
   *   "balance",
   *   { id: "GA7QYNF7SOWQ3GLR2BGMZEHXAVIRZA4KVWLTJJFC7MGXUA74P7UJVSGZ" },
   * );
   * ```
   */
  public async queryContract<T = any>(
    contractId: string,
    method: string,
    args: Record<string, unknown> = {},
    networkPassphrase?: string,
  ): Promise<{ result: T; isReadCall: boolean }> {
    const passphrase =
      networkPassphrase ?? (await this.getNetwork()).passphrase;

    // Dynamically import to avoid a static circular dependency: the contract
    // module imports this rpc module. The import resolves at call time, by
    // which point this module has finished loading.
    const { Client } = await import("../contract/client.js");

    // `Client.from` is SAC-aware: it uses the embedded SAC spec for built-in
    // Stellar Asset Contracts and downloads Wasm otherwise. We pass
    // `server: this` so the call reuses this server's transport.
    const client = await Client.from({
      contractId,
      rpcUrl: this.serverURL.toString(),
      networkPassphrase: passphrase,
      server: this,
    });

    // Validate against the contract spec (keyed by the real on-chain name)
    // first, so a `method` that collides with a built-in `Client`/prototype
    // member (e.g. `txFromJson`, `toString`) is rejected rather than silently
    // invoking the wrong function.
    const isContractMethod = client.spec
      .funcs()
      .some((fn) => fn.name.toString() === method);

    // Methods are attached dynamically from the spec, so they aren't on the
    // static `Client` type — hence the cast. The `Client` constructor attaches
    // each method under a sanitized identifier (e.g. a contract method named
    // `delete` becomes `delete_`), so resolve the key the same way; the method
    // still invokes under its real on-chain name.
    const { sanitizeIdentifier } = await import("../bindings/utils.js");
    const invoke = (client as unknown as Record<string, unknown>)[
      sanitizeIdentifier(method)
    ];

    if (!isContractMethod || typeof invoke !== "function") {
      throw new TypeError(`Contract ${contractId} has no method '${method}'`);
    }

    // Awaiting builds + simulates the read-only call (`simulate` defaults to
    // true); `.result` is the spec-decoded return value and `.isReadCall`
    // reports whether this call wrote no state and needed no auth.
    const assembled = await invoke(args);
    return { result: assembled.result as T, isReadCall: assembled.isReadCall };
  }

  /**
   * Lists a contract's callable methods and their signatures.
   *
   * A discovery helper for tooling, dapps, and agents that need to inspect an
   * arbitrary contract without knowing its interface up front. It resolves the
   * contract's spec (embedded in the Wasm for regular contracts, or the
   * built-in spec for Stellar Asset Contracts — see {@link contract.Client.from})
   * and reports each declared function's name, inputs, and outputs. No method
   * is invoked or simulated; this performs only the spec lookup.
   *
   * The complement to {@link queryContract}: list methods here, then call a
   * read-only one with `server.queryContract(contractId, method, args?)`.
   *
   * @param contractId - the contract to inspect (`C...`)
   * @param networkPassphrase - (optional) the network passphrase. If omitted, a
   *    request about network information will be made (see {@link getNetwork}).
   *    You can refer to {@link Networks} for a list of built-in passphrases,
   *    e.g., `Networks.TESTNET`.
   * @returns The contract's methods, in the order they appear in the spec
   *
   * @example
   * ```ts
   * const methods = await server.getContractMethods(
   *   "CCJZ5DGASBWQXR5MPFCJXMBI333XE5U3FSJTNQU7RIKE3P5GN2K2WYD5",
   * );
   * // [
   * //   { name: "decimals", inputs: [], outputs: ["U32"] },
   * //   { name: "balance", inputs: [{ name: "id", type: "Address" }], outputs: ["I128"] },
   * //   { name: "transfer", inputs: [...], outputs: [] },
   * // ]
   * ```
   */
  public async getContractMethods(
    contractId: string,
    networkPassphrase?: string,
  ): Promise<Api.ContractMethod[]> {
    const passphrase =
      networkPassphrase ?? (await this.getNetwork()).passphrase;

    // Dynamically import to avoid a static circular dependency: the contract
    // module imports this rpc module. The import resolves at call time, by
    // which point this module has finished loading.
    const { Client } = await import("../contract/client.js");

    // `Client.from` is SAC-aware: it uses the embedded SAC spec for built-in
    // Stellar Asset Contracts and downloads Wasm otherwise. We pass
    // `server: this` so the call reuses this server's transport.
    const client = await Client.from({
      contractId,
      rpcUrl: this.serverURL.toString(),
      networkPassphrase: passphrase,
      server: this,
    });

    return client.spec.funcs().map((fn) => {
      const doc = fn.doc.toString();
      const method: Api.ContractMethod = {
        name: fn.name.toString(),
        inputs: fn.inputs.map((input) => ({
          name: input.name.toString(),
          type: contractSpecTypeName(input.type),
        })),
        outputs: fn.outputs.map(contractSpecTypeName),
      };
      if (doc) {
        method.doc = doc;
      }
      return method;
    });
  }

  /**
   * Reads the current value of arbitrary ledger entries directly.
   *
   * Allows you to directly inspect the current state of contracts, contract's
   * code, accounts, or any other ledger entries.
   *
   * To fetch a contract's WASM byte-code, built the appropriate
   * {@link xdr.LedgerKeyContractCode} ledger entry key (or see
   * {@link Contract.getFootprint}).
   *
   * @param keys - One or more ledger entry keys to load
   * @returns The current on-chain
   * values for the given ledger keys
   *
   * @see {@link https://developers.stellar.org/docs/data/rpc/api-reference/methods/getLedgerEntries | getLedgerEntries docs}
   * @see RpcServer._getLedgerEntries
   * @example
   * ```ts
   * const contractId = "CAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAD2KM";
   * const key = xdr.LedgerKey.contractData(new xdr.LedgerKeyContractData({
   *   contractId: StrKey.decodeContract(contractId),
   *   key: xdr.ScVal.scvSymbol("counter"),
   * }));
   *
   * server.getLedgerEntries([key]).then(response => {
   *   const ledgerData = response.entries[0];
   *   console.log("key:", ledgerData.key);
   *   console.log("value:", ledgerData.val);
   *   console.log("liveUntilLedgerSeq:", ledgerData.liveUntilLedgerSeq);
   *   console.log("lastModified:", ledgerData.lastModifiedLedgerSeq);
   *   console.log("latestLedger:", response.latestLedger);
   * });
   * ```
   */
  public getLedgerEntries(...keys: LedgerKey[]) {
    return this._getLedgerEntries(...keys).then(parseRawLedgerEntries);
  }

  public _getLedgerEntries(...keys: LedgerKey[]) {
    return jsonrpc.postObject<Api.RawGetLedgerEntriesResponse>(
      this.httpClient,
      this.serverURL.toString(),
      "getLedgerEntries",
      {
        keys: keys.map((k) => k.toXdr("base64")),
      },
    );
  }

  public async getLedgerEntry(key: LedgerKey) {
    const results = await this._getLedgerEntries(key).then(
      parseRawLedgerEntries,
    );
    if (results.entries.length !== 1) {
      throw new Error(`failed to find an entry for key ${key.toXdr("base64")}`);
    }
    return results.entries[0];
  }

  /**
   * Poll for a particular transaction with certain parameters.
   *
   * After submitting a transaction, clients can use this to poll for
   * transaction completion and return a definitive state of success or failure.
   *
   * @param hash - the transaction you're polling for
   * @param opts - (optional) polling options
   *   - `attempts` (optional): (optional) the number of attempts to make
   *    before returning the last-seen status. By default or on invalid inputs,
   *    try 5 times.
   *   - `sleepStrategy` (optional): (optional) the amount of time
   *    to wait for between each attempt. By default, sleep for 1 second between
   *    each attempt.
   *
   * @returns the response after a "found"
   *    response (which may be success or failure) or the last response obtained
   *    after polling the maximum number of specified attempts.
   *
   * @example
   * ```ts
   * const h = "c4515e3bdc0897f21cc5dbec8c82cf0a936d4741cb74a8e158eb51b9fb00411a";
   * const txStatus = await server.pollTransaction(h, {
   *    attempts: 100, // I'm a maniac
   *    sleepStrategy: rpc.LinearSleepStrategy
   * }); // this will take 5,050 seconds to complete
   * ```
   */
  public async pollTransaction(
    hash: string,
    opts?: RpcServer.PollingOptions,
  ): Promise<Api.GetTransactionResponse> {
    const maxAttempts: number =
      (opts?.attempts ?? 0) < 1
        ? DEFAULT_GET_TRANSACTION_TIMEOUT
        : (opts?.attempts ?? DEFAULT_GET_TRANSACTION_TIMEOUT); // "positive and defined user value or default"

    let foundInfo: Api.GetTransactionResponse;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      foundInfo = await this.getTransaction(hash);
      if (foundInfo.status !== Api.GetTransactionStatus.NOT_FOUND) {
        return foundInfo;
      }

      await Utils.sleep((opts?.sleepStrategy ?? BasicSleepStrategy)(attempt));
    }

    return foundInfo!;
  }

  /**
   * Fetch the details of a submitted transaction.
   *
   * After submitting a transaction, clients should poll this to tell when the
   * transaction has completed.
   *
   * @param hash - Hex-encoded hash of the transaction to check
   * @returns The status, result, and
   *    other details about the transaction
   *
   * @see
   * {@link https://developers.stellar.org/docs/data/rpc/api-reference/methods/getTransaction | getTransaction docs}
   *
   * @example
   * ```ts
   * const transactionHash = "c4515e3bdc0897f21cc5dbec8c82cf0a936d4741cb74a8e158eb51b9fb00411a";
   * server.getTransaction(transactionHash).then((tx) => {
   *   console.log("status:", tx.status);
   *   console.log("envelopeXdr:", tx.envelopeXdr);
   *   console.log("resultMetaXdr:", tx.resultMetaXdr);
   *   console.log("resultXdr:", tx.resultXdr);
   * });
   * ```
   */

  public async getTransaction(
    hash: string,
  ): Promise<Api.GetTransactionResponse> {
    return this._getTransaction(hash).then((raw) => {
      const foundInfo: Omit<
        Api.GetSuccessfulTransactionResponse,
        keyof Api.GetMissingTransactionResponse
      > = {} as any;

      if (raw.status !== Api.GetTransactionStatus.NOT_FOUND) {
        Object.assign(foundInfo, parseTransactionInfo(raw));
      }

      const result: Api.GetTransactionResponse = {
        status: raw.status,
        txHash: hash,
        latestLedger: raw.latestLedger,
        latestLedgerCloseTime: raw.latestLedgerCloseTime,
        oldestLedger: raw.oldestLedger,
        oldestLedgerCloseTime: raw.oldestLedgerCloseTime,
        ...foundInfo,
      };

      return result;
    });
  }

  public async _getTransaction(
    hash: string,
  ): Promise<Api.RawGetTransactionResponse> {
    return jsonrpc.postObject(
      this.httpClient,
      this.serverURL.toString(),
      "getTransaction",
      {
        hash,
      },
    );
  }

  /**
   * Fetch transactions starting from a given start ledger or a cursor. The end ledger is the latest ledger
   * in that RPC instance.
   *
   * @param request - The request parameters.
   * @returns - A promise that resolves to the transactions response.
   *
   * @see https://developers.stellar.org/docs/data/rpc/api-reference/methods/getTransactions
   * @example
   * ```ts
   * server.getTransactions({
   *   startLedger: 10000,
   *   limit: 10,
   * }).then((response) => {
   *   console.log("Transactions:", response.transactions);
   *   console.log("Latest Ledger:", response.latestLedger);
   *   console.log("Cursor:", response.cursor);
   * });
   * ```
   */
  public async getTransactions(
    request: Api.GetTransactionsRequest,
  ): Promise<Api.GetTransactionsResponse> {
    return this._getTransactions(request).then(
      (raw: Api.RawGetTransactionsResponse) => {
        const result: Api.GetTransactionsResponse = {
          transactions: (raw.transactions || []).map(parseRawTransactions),
          latestLedger: raw.latestLedger,
          latestLedgerCloseTimestamp: raw.latestLedgerCloseTimestamp,
          oldestLedger: raw.oldestLedger,
          oldestLedgerCloseTimestamp: raw.oldestLedgerCloseTimestamp,
          cursor: raw.cursor,
        };
        return result;
      },
    );
  }

  async _getTransactions(
    request: Api.GetTransactionsRequest,
  ): Promise<Api.RawGetTransactionsResponse> {
    return jsonrpc.postObject(
      this.httpClient,
      this.serverURL.toString(),
      "getTransactions",
      request,
    );
  }

  /**
   * Fetch all events that match a given set of filters.
   *
   * The given filters (see {@link Api.EventFilter}
   * for detailed fields) are combined only in a logical OR fashion, and all of
   * the fields in each filter are optional.
   *
   * To page through events, use the `pagingToken` field on the relevant
   * {@link Api.EventResponse} object to set the `cursor` parameter.
   *
   * @param request - Event filters {@link Api.GetEventsRequest},
   * @returns A paginatable set of the events
   * matching the given event filters
   *
   * @see {@link https://developers.stellar.org/docs/data/rpc/api-reference/methods/getEvents | getEvents docs}
   *
   * @example
   * ```ts
   *
   * server.getEvents({
   *    startLedger: 1000,
   *    endLedger: 2000,
   *    filters: [
   *     {
   *      type: "contract",
   *      contractIds: [ "deadb33f..." ],
   *      topics: [[ "AAAABQAAAAh0cmFuc2Zlcg==", "AAAAAQB6Mcc=", "*" ]]
   *     }, {
   *      type: "system",
   *      contractIds: [ "...c4f3b4b3..." ],
   *      topics: [[ "*" ], [ "*", "AAAAAQB6Mcc=" ]]
   *     }, {
   *      contractIds: [ "...c4f3b4b3..." ],
   *      topics: [[ "AAAABQAAAAh0cmFuc2Zlcg==" ]]
   *     }, {
   *      type: "diagnostic",
   *      topics: [[ "AAAAAQB6Mcc=" ]]
   *     }
   *    ],
   *    limit: 10,
   * });
   * ```
   */

  public async getEvents(
    request: Api.GetEventsRequest,
  ): Promise<Api.GetEventsResponse> {
    return this._getEvents(request).then(parseRawEvents);
  }

  public async _getEvents(
    request: Api.GetEventsRequest,
  ): Promise<Api.RawGetEventsResponse> {
    return jsonrpc.postObject(
      this.httpClient,
      this.serverURL.toString(),
      "getEvents",
      {
        filters: request.filters ?? [],
        pagination: {
          ...(request.cursor && { cursor: request.cursor }), // add if defined
          ...(request.limit && { limit: request.limit }),
        },
        ...(request.startLedger && {
          startLedger: request.startLedger,
        }),
        ...(request.endLedger && {
          endLedger: request.endLedger,
        }),
      },
    );
  }

  /**
   * Fetch one page of contract and system events with the `queryEvents`
   * method, which only the archival RPC node serves (in beta). To page, send
   * `{ cursor, limit }` from the response while `scanStatus` is `HAS_MORE`,
   * wait and retry on `WAITING_FOR_LEDGERS`, and stop on `OLDEST_REACHED` or
   * `COMPLETE`.
   *
   * @param request - a ledger range query or a cursor continuation
   * @returns one page of parsed events and the scan status
   *
   * @example
   * ```ts
   * let res = await server.queryEvents({ minLedger: 1000, limit: 50 });
   * for (;;) {
   *   handle(res.events);
   *   if (res.scanStatus === rpc.Api.QueryEventsScanStatus.WAITING_FOR_LEDGERS) {
   *     await new Promise((resolve) => setTimeout(resolve, 5000));
   *   } else if (res.scanStatus !== rpc.Api.QueryEventsScanStatus.HAS_MORE) {
   *     break;
   *   }
   *   res = await server.queryEvents({ cursor: res.cursor!, limit: 50 });
   * }
   * ```
   */
  public async queryEvents(
    request: Api.QueryEventsRequest,
  ): Promise<Api.QueryEventsResponse> {
    return this._queryEvents(encodeQueryEventsRequest(request)).then(
      parseRawQueryEvents,
    );
  }

  public async _queryEvents(
    request: Api.RawQueryEventsRequest,
  ): Promise<Api.RawQueryEventsResponse> {
    return jsonrpc.postObject(
      this.httpClient,
      this.serverURL.toString(),
      "queryEvents",
      request,
    );
  }

  /**
   * Fetch metadata about the network this Soroban RPC server is connected to.
   *
   * @returns Metadata about the current
   * network this RPC server is connected to
   *
   * @see {@link https://developers.stellar.org/docs/data/rpc/api-reference/methods/getNetwork | getNetwork docs}
   *
   * @example
   * ```ts
   * server.getNetwork().then((network) => {
   *   console.log("friendbotUrl:", network.friendbotUrl);
   *   console.log("passphrase:", network.passphrase);
   *   console.log("protocolVersion:", network.protocolVersion);
   * });
   * ```
   */

  public async getNetwork(): Promise<Api.GetNetworkResponse> {
    return jsonrpc.postObject(
      this.httpClient,
      this.serverURL.toString(),
      "getNetwork",
    );
  }

  /**
   * Fetch the latest ledger meta info from network which this Soroban RPC
   * server is connected to.
   *
   * @returns metadata about the
   *    latest ledger on the network that this RPC server is connected to
   *
   * @see {@link https://developers.stellar.org/docs/data/rpc/api-reference/methods/getLatestLedger | getLatestLedger docs}
   *
   * @example
   * ```ts
   * server.getLatestLedger().then((response) => {
   *   console.log("hash:", response.id);
   *   console.log("sequence:", response.sequence);
   *   console.log("protocolVersion:", response.protocolVersion);
   * });
   * ```
   */
  public async getLatestLedger(): Promise<Api.GetLatestLedgerResponse> {
    return this._getLatestLedger().then(parseRawLatestLedger);
  }

  public async _getLatestLedger(): Promise<Api.RawGetLatestLedgerResponse> {
    return jsonrpc.postObject(
      this.httpClient,
      this.serverURL.toString(),
      "getLatestLedger",
    );
  }

  /**
   * Submit a trial contract invocation to get back return values, expected
   * ledger footprint, expected authorizations, and expected costs.
   *
   * @param tx - the transaction to simulate,
   *    which should include exactly one operation (one of
   *    {@link xdr.InvokeHostFunctionOp}, {@link xdr.ExtendFootprintTtlOp}, or
   *    {@link xdr.RestoreFootprintOp}). Any provided footprint or auth
   *    information will be ignored.
   * @param addlResources - (optional) any additional resources
   *    to add to the simulation-provided ones, for example if you know you will
   *    need extra CPU instructions
   * @param authMode - (optional) optionally, specify the type of
   *    auth mode to use for simulation: `enforce` for enforcement mode,
   *    `record` for recording mode, or `record_allow_nonroot` for recording
   *    mode that allows non-root authorization
   * @param useUpgradedAuth - (optional) whether simulation records v2 address
   *    credentials (CAP-71) instead of the legacy v1 address credentials.
   *    Defaults to `true`; pass `false` to ask for the legacy v1 format.
   *    It only affects the recording auth modes.
   *
   *    **Deprecated**: this flag is transitional. Once the network returns v2
   *    credentials by default (protocol 28), it becomes a no-op — do not rely
   *    on passing `false` to keep receiving the legacy v1 format.
   *
   * @returns An object with the
   *    cost, footprint, result/auth requirements (if applicable), and error of
   *    the transaction
   *
   * @see
   * {@link https://developers.stellar.org/docs/learn/fundamentals/stellar-data-structures/operations-and-transactions | transaction docs}
   * @see
   * {@link https://developers.stellar.org/docs/data/rpc/api-reference/methods/simulateTransaction | simulateTransaction docs}
   * @see
   * {@link https://developers.stellar.org/docs/learn/fundamentals/contract-development/contract-interactions/transaction-simulation#authorization | authorization modes}
   * @see module:rpc.Server#prepareTransaction
   * @see module:rpc.assembleTransaction
   *
   * @example
   * ```ts
   * const contractId = 'CA3D5KRYM6CB7OWQ6TWYRR3Z4T7GNZLKERYNZGGA5SOAOPIFY6YQGAXE';
   * const contract = new StellarSdk.Contract(contractId);
   *
   * // Right now, this is just the default fee for this example.
   * const fee = StellarSdk.BASE_FEE;
   * const transaction = new StellarSdk.TransactionBuilder(account, { fee })
   *   // Uncomment the following line to build transactions for the live network. Be
   *   // sure to also change the horizon hostname.
   *   //.setNetworkPassphrase(StellarSdk.Networks.PUBLIC)
   *   .setNetworkPassphrase(StellarSdk.Networks.FUTURENET)
   *   .setTimeout(30) // valid for the next 30s
   *   // Add an operation to call increment() on the contract
   *   .addOperation(contract.call("increment"))
   *   .build();
   *
   * server.simulateTransaction(transaction).then((sim) => {
   *   console.log("cost:", sim.cost);
   *   console.log("result:", sim.result);
   *   console.log("error:", sim.error);
   *   console.log("latestLedger:", sim.latestLedger);
   * });
   * ```
   */

  public async simulateTransaction(
    tx: Transaction | FeeBumpTransaction,
    addlResources?: RpcServer.ResourceLeeway,
    authMode?: Api.SimulationAuthMode,
    useUpgradedAuth: boolean = true,
  ): Promise<Api.SimulateTransactionResponse> {
    return this._simulateTransaction(
      tx,
      addlResources,
      authMode,
      useUpgradedAuth,
    ).then(parseRawSimulation);
  }

  public async _simulateTransaction(
    transaction: Transaction | FeeBumpTransaction,
    addlResources?: RpcServer.ResourceLeeway,
    authMode?: Api.SimulationAuthMode,
    useUpgradedAuth: boolean = true,
  ): Promise<Api.RawSimulateTransactionResponse> {
    return jsonrpc.postObject(
      this.httpClient,
      this.serverURL.toString(),
      "simulateTransaction",
      {
        transaction: transaction.toXdr(),
        authMode,
        useUpgradedAuth,
        ...(addlResources !== undefined && {
          resourceConfig: {
            instructionLeeway: addlResources.cpuInstructions,
          },
        }),
      },
    );
  }

  /**
   * Submit a trial contract invocation, first run a simulation of the contract
   * invocation as defined on the incoming transaction, and apply the results to
   * a new copy of the transaction which is then returned. Setting the ledger
   * footprint and authorization, so the resulting transaction is ready for
   * signing & sending.
   *
   * The returned transaction will also have an updated fee that is the sum of
   * fee set on incoming transaction with the contract resource fees estimated
   * from simulation. It is advisable to check the fee on returned transaction
   * and validate or take appropriate measures for interaction with user to
   * confirm it is acceptable.
   *
   * You can call the {@link rpc.Server.simulateTransaction} method
   * directly first if you want to inspect estimated fees for a given
   * transaction in detail first, then re-assemble it manually or via
   * {@link rpc.assembleTransaction}.
   *
   * @param tx - the transaction to
   *    prepare. It should include exactly one operation, which must be one of
   *    {@link xdr.InvokeHostFunctionOp}, {@link xdr.ExtendFootprintTtlOp},
   *    or {@link xdr.RestoreFootprintOp}.
   *
   *    Any provided footprint will be overwritten. However, if your operation
   *    has existing auth entries, they will be preferred over ALL auth entries
   *    from the simulation. In other words, if you include auth entries, you
   *    don't care about the auth returned from the simulation. Other fields
   *    (footprint, etc.) will be filled as normal.
   * @param useUpgradedAuth - (optional) whether the underlying simulation
   *    records v2 address credentials (CAP-71) instead of the legacy v1
   *    address credentials. Defaults to `true`; pass `false` to ask for the
   *    legacy v1 format. It only affects the recording auth modes.
   *
   *    **Deprecated**: this flag is transitional. Once the RPC server returns v2
   *    credentials by default (protocol 28), it becomes a no-op — do not rely
   *    on passing `false` to keep receiving the legacy v1 format.
   * @returns A copy of the
   *    transaction with the expected authorizations (in the case of
   *    invocation), resources, and ledger footprints added. The transaction fee
   *    will also automatically be padded with the contract's minimum resource
   *    fees discovered from the simulation.
   * @throws    *    If simulation fails
   *
   * @see module:rpc.assembleTransaction
   * @see {@link https://developers.stellar.org/docs/data/rpc/api-reference/methods/simulateTransaction | simulateTransaction docs}
   *
   * @example
   * ```ts
   * const contractId = 'CA3D5KRYM6CB7OWQ6TWYRR3Z4T7GNZLKERYNZGGA5SOAOPIFY6YQGAXE';
   * const contract = new StellarSdk.Contract(contractId);
   *
   * // Right now, this is just the default fee for this example.
   * const fee = StellarSdk.BASE_FEE;
   * const transaction = new StellarSdk.TransactionBuilder(account, { fee })
   *   // Uncomment the following line to build transactions for the live network. Be
   *   // sure to also change the horizon hostname.
   *   //.setNetworkPassphrase(StellarSdk.Networks.PUBLIC)
   *   .setNetworkPassphrase(StellarSdk.Networks.FUTURENET)
   *   .setTimeout(30) // valid for the next 30s
   *   // Add an operation to call increment() on the contract
   *   .addOperation(contract.call("increment"))
   *   .build();
   *
   * const preparedTransaction = await server.prepareTransaction(transaction);
   *
   * // Sign this transaction with the secret key
   * // NOTE: signing is transaction is network specific. Test network transactions
   * // won't work in the public network. To switch networks, use the Network object
   * // as explained above (look for StellarSdk.Network).
   * const sourceKeypair = StellarSdk.Keypair.fromSecret(sourceSecretKey);
   * preparedTransaction.sign(sourceKeypair);
   *
   * server.sendTransaction(transaction).then(result => {
   *   console.log("hash:", result.hash);
   *   console.log("status:", result.status);
   *   console.log("errorResultXdr:", result.errorResultXdr);
   * });
   * ```
   */
  public async prepareTransaction(
    tx: Transaction | FeeBumpTransaction,
    useUpgradedAuth: boolean = true,
  ) {
    const simResponse = await this.simulateTransaction(
      tx,
      undefined,
      undefined,
      useUpgradedAuth,
    );
    if (Api.isSimulationError(simResponse)) {
      throw new Error(simResponse.error);
    }

    return assembleTransaction(tx, simResponse).build();
  }

  /**
   * Submit a real transaction to the Stellar network.
   *
   * Unlike Horizon, RPC does not wait for transaction completion. It
   * simply validates the transaction and enqueues it. Clients should call
   * {@link rpc.Server.getTransaction} to learn about transaction
   * success/failure.
   *
   * @param transaction - to submit
   * @returns the
   *    transaction id, status, and any error if available
   *
   * @see {@link https://developers.stellar.org/docs/learn/fundamentals/stellar-data-structures/operations-and-transactions | transaction docs}
   * @see {@link https://developers.stellar.org/docs/data/rpc/api-reference/methods/sendTransaction | sendTransaction docs}
   *
   * @example
   * ```ts
   * const contractId = 'CA3D5KRYM6CB7OWQ6TWYRR3Z4T7GNZLKERYNZGGA5SOAOPIFY6YQGAXE';
   * const contract = new StellarSdk.Contract(contractId);
   *
   * // Right now, this is just the default fee for this example.
   * const fee = StellarSdk.BASE_FEE;
   * const transaction = new StellarSdk.TransactionBuilder(account, { fee })
   *   // Uncomment the following line to build transactions for the live network. Be
   *   // sure to also change the horizon hostname.
   *   //.setNetworkPassphrase(StellarSdk.Networks.PUBLIC)
   *   .setNetworkPassphrase(StellarSdk.Networks.FUTURENET)
   *   .setTimeout(30) // valid for the next 30s
   *   // Add an operation to call increment() on the contract
   *   .addOperation(contract.call("increment"))
   *   .build();
   *
   * // Sign this transaction with the secret key
   * // NOTE: signing is transaction is network specific. Test network transactions
   * // won't work in the public network. To switch networks, use the Network object
   * // as explained above (look for StellarSdk.Network).
   * const sourceKeypair = StellarSdk.Keypair.fromSecret(sourceSecretKey);
   * transaction.sign(sourceKeypair);
   *
   * server.sendTransaction(transaction).then((result) => {
   *   console.log("hash:", result.hash);
   *   console.log("status:", result.status);
   *   console.log("errorResultXdr:", result.errorResultXdr);
   * });
   * ```
   */
  public async sendTransaction(
    transaction: Transaction | FeeBumpTransaction,
  ): Promise<Api.SendTransactionResponse> {
    return this._sendTransaction(transaction).then(parseRawSendTransaction);
  }

  public async _sendTransaction(
    transaction: Transaction | FeeBumpTransaction,
  ): Promise<Api.RawSendTransactionResponse> {
    return jsonrpc.postObject(
      this.httpClient,
      this.serverURL.toString(),
      "sendTransaction",
      {
        transaction: transaction.toXdr(),
      },
    );
  }

  /**
   * Fund a new account using the network's Friendbot faucet, if any.
   *
   * @param address - The address or account instance that we
   *    want to create and fund with Friendbot
   * @param friendbotUrl - (optional) Optionally, an explicit address for
   *    friendbot (by default: this calls the Soroban RPC
   *    {@link rpc.Server.getNetwork | getNetwork} method to try to
   *    discover this network's Friendbot url).
   * @returns An {@link Account} object for the created
   *    account, or the existing account if it's already funded with the
   *    populated sequence number (note that the account will not be "topped
   *    off" if it already exists)
   * @throws If Friendbot is not configured on this network or request failure
   *
   * @see {@link https://developers.stellar.org/docs/learn/fundamentals/networks#friendbot | Friendbot docs}
   * @see {@link Friendbot.Api.Response}
   *
   * @deprecated Use {@link Server.fundAddress} instead, which supports both
   *    account (G...) and contract (C...) addresses.
   *
   * @example
   * ```ts
   * server
   *    .requestAirdrop("GBZC6Y2Y7Q3ZQ2Y4QZJ2XZ3Z5YXZ6Z7Z2Y4QZJ2XZ3Z5YXZ6Z7Z2Y4")
   *    .then((accountCreated) => {
   *      console.log("accountCreated:", accountCreated);
   *    }).catch((error) => {
   *      console.error("error:", error);
   *    });
   * ```
   */
  public async requestAirdrop(
    address: string | Pick<Account, "accountId">,
    friendbotUrl?: string,
  ): Promise<Account> {
    const account = typeof address === "string" ? address : address.accountId();
    friendbotUrl = friendbotUrl || (await this.getNetwork()).friendbotUrl;
    if (!friendbotUrl) {
      throw new Error("No friendbot URL configured for current network");
    }

    try {
      const response = await this.httpClient.post<FriendbotApi.Response>(
        `${friendbotUrl}?addr=${encodeURIComponent(account)}`,
      );

      let meta: TransactionMeta;
      if (!response.data.result_meta_xdr) {
        const txMeta = await this.getTransaction(response.data.hash);
        if (txMeta.status !== Api.GetTransactionStatus.SUCCESS) {
          throw new Error(`Funding account ${address} failed`);
        }
        meta = txMeta.resultMetaXdr;
      } else {
        meta = TransactionMeta.fromXdr(response.data.result_meta_xdr, "base64");
      }

      const sequence = findCreatedAccountSequenceInTransactionMeta(meta);
      return new Account(account, sequence);
    } catch (error: any) {
      if (error.response?.status === 400) {
        if (
          error.response.data?.detail?.includes("createAccountAlreadyExist")
        ) {
          // Account already exists, load the sequence number
          return this.getAccount(account);
        }
      }
      throw error;
    }
  }

  /**
   * Fund an address using the network's Friendbot faucet, if any.
   *
   * This method supports both account (G...) and contract (C...) addresses.
   *
   * @param address - The address to fund. Can be either a Stellar
   *    account (G...) or contract (C...) address.
   * @param friendbotUrl - (optional) Optionally, an explicit Friendbot URL
   *    (by default: this calls the Stellar RPC
   *    {@link rpc.Server.getNetwork | getNetwork} method to try to
   *    discover this network's Friendbot url).
   * @returns The transaction
   *    response from the Friendbot funding transaction.
   * @throws If Friendbot is not configured on this network or the
   *    funding transaction fails.
   *
   * @see {@link https://developers.stellar.org/docs/learn/fundamentals/networks#friendbot | Friendbot docs}
   *
   * @example
   * ```ts
   * // Funding an account (G... address)
   * const tx = await server.fundAddress("GBZC6Y2Y7...");
   * console.log("Funded! Hash:", tx.txHash);
   * // If you need the Account object:
   * const account = await server.getAccount("GBZC6Y2Y7...");
   * ```
   *
   * @example
   * ```ts
   * // Funding a contract (C... address)
   * const tx = await server.fundAddress("CBZC6Y2Y7...");
   * console.log("Contract funded! Hash:", tx.txHash);
   * ```
   */
  public async fundAddress(
    address: string,
    friendbotUrl?: string,
  ): Promise<Api.GetSuccessfulTransactionResponse> {
    if (
      !StrKey.isValidEd25519PublicKey(address) &&
      !StrKey.isValidContract(address)
    ) {
      throw new Error(
        `Invalid address: ${address}. Expected a Stellar account (G...) or contract (C...) address.`,
      );
    }

    friendbotUrl = friendbotUrl || (await this.getNetwork()).friendbotUrl;
    if (!friendbotUrl) {
      throw new Error("No friendbot URL configured for current network");
    }

    try {
      const response = await this.httpClient.post<FriendbotApi.Response>(
        `${friendbotUrl}?addr=${encodeURIComponent(address)}`,
      );

      const txResponse = await this.getTransaction(response.data.hash);
      if (txResponse.status !== Api.GetTransactionStatus.SUCCESS) {
        throw new Error(
          `Funding address ${address} failed: transaction status ${txResponse.status}`,
        );
      }

      return txResponse;
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error(error.response.data?.detail ?? "Bad Request");
      }
      throw error;
    }
  }

  /**
   * Provides an analysis of the recent fee stats for regular and smart
   * contract operations.
   *
   * @returns the fee stats
   * @see https://developers.stellar.org/docs/data/rpc/api-reference/methods/getFeeStats
   */
  public async getFeeStats(): Promise<Api.GetFeeStatsResponse> {
    return jsonrpc.postObject(
      this.httpClient,
      this.serverURL.toString(),
      "getFeeStats",
    );
  }

  /**
   * Provides information about the current version details of the Soroban RPC and captive-core
   *
   * @returns the version info
   * @see https://developers.stellar.org/docs/data/rpc/api-reference/methods/getVersionInfo
   */
  public async getVersionInfo(): Promise<Api.GetVersionInfoResponse> {
    return jsonrpc.postObject(
      this.httpClient,
      this.serverURL.toString(),
      "getVersionInfo",
    );
  }

  /**
   * Returns a contract's balance of a particular SAC asset, if any.
   *
   * This is a convenience wrapper around {@link Server.getLedgerEntries}.
   *
   * @param address - the contract (string `C...`) whose balance of
   *    `sac` you want to know
   * @param sac - the built-in SAC token (e.g. `USDC:GABC...`) that
   *    you are querying from the given `contract`.
   * @param networkPassphrase - (optional) optionally, the network passphrase to
   *    which this token applies. If omitted, a request about network
   *    information will be made (see {@link getNetwork}), since contract IDs
   *    for assets are specific to a network. You can refer to {@link Networks}
   *    for a list of built-in passphrases, e.g., `Networks.TESTNET`.
   *
   * @returns , which will contain the balance
   *    entry details if and only if the request returned a valid balance ledger
   *    entry. If it doesn't, the `balanceEntry` field will not exist.
   *
   * @throws If `address` is not a valid contract ID (`C...`).
   *
   * @see getLedgerEntries
   * @see https://developers.stellar.org/docs/tokens/stellar-asset-contract
   *
   * @deprecated Use {@link getAssetBalance}, instead
   * @example
   * ```ts
   * // assume `address` is some contract or account with an XLM balance
   * // assume server is an instantiated `Server` instance.
   * const entry = (await server.getSACBalance(
   *   new Address(address),
   *   Asset.native(),
   *   Networks.PUBLIC
   * ));
   *
   * // assumes BigInt support:
   * console.log(
   *   entry.balanceEntry ?
   *   BigInt(entry.balanceEntry.amount) :
   *   "Address has no XLM");
   * ```
   */
  public async getSACBalance(
    address: string | Address,
    sac: Asset,
    networkPassphrase?: string,
  ): Promise<Api.BalanceResponse> {
    const addressString =
      address instanceof Address ? address.toString() : address;

    if (!StrKey.isValidContract(addressString)) {
      assertNotMuxed(
        addressString,
        "has no balance of its own; use its base address",
      );
      throw new TypeError(`expected contract ID, got ${addressString}`);
    }

    // Call out to RPC if passphrase isn't provided.
    const passphrase: string =
      networkPassphrase ?? (await this.getNetwork().then((n) => n.passphrase));

    // Turn SAC into predictable contract ID
    const sacId = sac.contractId(passphrase);

    // Rust union enum type with "Balance(ScAddress)" structure
    const key = nativeToScVal(["Balance", addressString], {
      type: ["symbol", "address"],
    });

    // Note a quirk here: the contract address in the key is the *token*
    // rather than the *holding contract*. This is because each token stores a
    // balance entry for each contract, not the other way around (i.e. XLM
    // holds a reserve for contract X, rather that contract X having a balance
    // of N XLM).
    const ledgerKey = LedgerKey.contractData(
      new LedgerKeyContractData({
        contract: new Address(sacId).toScAddress(),
        durability: ContractDataDurability.persistent,
        key,
      }),
    );

    const response = await this.getLedgerEntries(ledgerKey);
    if (response.entries.length === 0) {
      return { latestLedger: response.latestLedger };
    }

    const { lastModifiedLedgerSeq, liveUntilLedgerSeq, val } =
      response.entries[0];

    if (val.type !== "contractData") {
      return { latestLedger: response.latestLedger };
    }

    const entry = scValToNative(val.value.val);

    // Since we are requesting a SAC's contract data, we know for a fact that
    // it should follow the expected structure format. Thus, we can presume
    // these fields exist:
    return {
      latestLedger: response.latestLedger,
      balanceEntry: {
        liveUntilLedgerSeq,
        lastModifiedLedgerSeq,
        amount: entry.amount.toString(),
        authorized: entry.authorized,
        clawback: entry.clawback,
      },
    };
  }

  /**
   * Fetch a detailed list of ledgers starting from a specified point.
   *
   * Returns ledger data with support for pagination as long as the requested
   * pages fall within the history retention of the RPC provider.
   *
   * @param request - The request parameters for fetching ledgers. {@link Api.GetLedgersRequest}
   * @returns A promise that resolves to the
   *    ledgers response containing an array of ledger data and pagination info. {@link Api.GetLedgersResponse}
   *
   * @throws If startLedger is less than the oldest ledger stored in this
   *    node, or greater than the latest ledger seen by this node.
   *
   * @see {@link https://developers.stellar.org/docs/data/rpc/api-reference/methods/getLedgers | getLedgers docs}
   *
   * @example
   * ```ts
   * // Fetch ledgers starting from a specific sequence number
   * server.getLedgers({
   *   startLedger: 36233,
   *   pagination: {
   *     limit: 10
   *   }
   * }).then((response) => {
   *   console.log("Ledgers:", response.ledgers);
   *   console.log("Latest Ledger:", response.latestLedger);
   *   console.log("Cursor:", response.cursor);
   * });
   * ```
   *
   * @example
   * ```ts
   * // Paginate through ledgers using cursor
   * const firstPage = await server.getLedgers({
   *   startLedger: 36233,
   *   pagination: {
   *     limit: 5
   *   }
   * });
   *
   * const nextPage = await server.getLedgers({
   *   pagination: {
   *     cursor: firstPage.cursor,
   *     limit: 5
   *   }
   * });
   * ```
   */

  public async getLedgers(
    request: Api.GetLedgersRequest,
  ): Promise<Api.GetLedgersResponse> {
    return this._getLedgers(request).then((raw) => {
      const result: Api.GetLedgersResponse = {
        ledgers: (raw.ledgers || []).map(parseRawLedger),
        latestLedger: raw.latestLedger,
        latestLedgerCloseTime: raw.latestLedgerCloseTime,
        oldestLedger: raw.oldestLedger,
        oldestLedgerCloseTime: raw.oldestLedgerCloseTime,
        cursor: raw.cursor,
      };
      return result;
    });
  }

  public async _getLedgers(
    request: Api.GetLedgersRequest,
  ): Promise<Api.RawGetLedgersResponse> {
    return jsonrpc.postObject(
      this.httpClient,
      this.serverURL.toString(),
      "getLedgers",
      request,
    );
  }
}
